import { Injectable } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormControl,
  AbstractControl
} from '@angular/forms';

import { BorrowerService } from '../../../../shared/services/borrower/borrower.service';
import { FormValidationService } from '../../../../shared/fork-common-controls/form-validation.service';
import { GetBorrowerResponse } from '../models/index';
import { SessionService } from '../../../../shared/services/session/shared/session/session-service/session.service';
import { UlaValidators } from '../../../../common-library/validators/ula-validators';
import { Utils } from '../../../../shared/fork-common-controls/utils.service';

@Injectable()
export class ApplyFormProvider {
  private static applyForm: FormGroup = null;
  private borrowerIsStale: boolean = true;

  constructor(
    private formBuilder: FormBuilder,
    private applyFormValidationService: FormValidationService,
    private borrowerService: BorrowerService,
    private sessionService: SessionService) {

    if (!ApplyFormProvider.applyForm) {
      this.buildForm();
    }

    this.fillForm();
  }

  private get employmentInfoControls(): { [key: string]: AbstractControl } {
    return this.getControls(this.employmentInfoControlNames);
  }

  private get incomeSourceControls(): { [key: string]: AbstractControl } {
    return this.getControls(this.incomeSourceControlNames);
  }

  getForm(): FormGroup {
    return ApplyFormProvider.applyForm;
  }

  isValid(): boolean {
    return ApplyFormProvider.applyForm.valid;
  }

  private get employmentInfoControlNames(): string[] {
    return [
      'employerName',
      'employmentPosition',
      'yearsWithEmployer',
      'employmentStartDate',
      'workAddress',
      'workAddressUnit',
      'workCity',
      'workCounty',
      'workCountry',
      'workState',
      'workZip',
      'workPhone',
      'workExtension'];
  }

  private get incomeSourceControlNames(): string[] {
    return [
      'rentalIncome',
      'interest',
      'socialSecurity',
      'alimony',
      'pension',
      'childSupport',
      'ira',
      'disability'];
  }

  private buildForm(): void {
    const employmentInfoControl = new FormControl('', Validators.required);
    const yearsWithEmployerControl = new FormControl(null, UlaValidators.getEmpDetailsYearsValidator(employmentInfoControl));

    ApplyFormProvider.applyForm = this.formBuilder.group({
      employmentInfo: employmentInfoControl,
      employerName: ['', UlaValidators.getEmpDetailsNameValidator(employmentInfoControl, this.applyFormValidationService)],
      employmentPosition: ['', UlaValidators.getEmpDetailsPositionValidator(employmentInfoControl, this.applyFormValidationService)],
      yearsWithEmployer: yearsWithEmployerControl,
      employmentStartDate: ['', UlaValidators.getEmpDetailsStartDateValidator(yearsWithEmployerControl)],
      workAddress: ['', UlaValidators.getAllAddressValidators()],
      workAddressUnit: [''],
      workCity: ['', UlaValidators.getEmpDetailsWorkCityValidator(employmentInfoControl, this.applyFormValidationService)],
      workCounty: [''],
      workCountry: ['US'],
      workState: ['', UlaValidators.getEmpDetailsWorkStateValidator(employmentInfoControl)],
      workZip: ['', UlaValidators.getEmpDetailsWorkZipValidator(employmentInfoControl, this.applyFormValidationService)],
      workPhone: ['', UlaValidators.getEmpDetailsWorkPhoneValidator(employmentInfoControl, this.applyFormValidationService)],
      workExtension: '',
      rentalIncome: false,
      interest: false,
      socialSecurity: false,
      alimony: false,
      pension: false,
      childSupport: false,
      ira: false,
      disability: false,
      acceptTerms: [null, UlaValidators.mustBeChecked],
      ssn: ['', UlaValidators.getAllSSNValidators(this.applyFormValidationService)]
    });
  }

  private fillForm(): void {
    this.borrowerService.getBorrower(this.borrowerIsStale)
      .subscribe(borrower => {
        const formControls = this.getControls();
        const currentEmployer = borrower.currentEmployer;
        const employerAddress = (borrower.currentEmployer.address || {});
        const incomeTypes = borrower.incomeTypes;
        const incomeTypesEnum = GetBorrowerResponse.IncomeTypesEnum;
        const yearsWithEmployer = (currentEmployer.lengthYears < 1 ? -1 : borrower.currentEmployer.lengthYears);

        this.sessionService.mortgageSession.data.borrowerData.primaryBorrowerData.employmentStatus =
          GetBorrowerResponse.EmploymentStatusEnum[borrower.employmentStatus];

        formControls['employmentInfo'].setValue(borrower.employmentStatus);
        formControls['employerName'].setValue(currentEmployer.name);
        formControls['employmentPosition'].setValue(currentEmployer.position);
        formControls['yearsWithEmployer'].setValue(yearsWithEmployer);
        formControls['employmentStartDate'].setValue(Utils.extractDateFromDateTime((currentEmployer.startDate || '').toString()));
        formControls['workAddress'].setValue(employerAddress.addressLine1);
        formControls['workAddressUnit'].setValue(employerAddress.addressLine2);
        formControls['workCity'].setValue(employerAddress.city);
        formControls['workState'].setValue(employerAddress.state);
        formControls['workZip'].setValue(employerAddress.zipCode);
        formControls['workPhone'].setValue(Utils.formatPhoneNumber(currentEmployer.phoneNumber));
        formControls['workExtension'].setValue(currentEmployer.extension);
        formControls['rentalIncome'].setValue(this.isSetIncomeType(incomeTypes, incomeTypesEnum.Rental));
        formControls['interest'].setValue(this.isSetIncomeType(incomeTypes, incomeTypesEnum.InterestOrDividends));
        formControls['socialSecurity'].setValue(this.isSetIncomeType(incomeTypes, incomeTypesEnum.SocialSecurity));
        formControls['alimony'].setValue(this.isSetIncomeType(incomeTypes, incomeTypesEnum.Alimony));
        formControls['pension'].setValue(this.isSetIncomeType(incomeTypes, incomeTypesEnum.PensionOrAnnuity));
        formControls['childSupport'].setValue(this.isSetIncomeType(incomeTypes, incomeTypesEnum.ChildSupport));
        formControls['ira'].setValue(this.isSetIncomeType(incomeTypes, incomeTypesEnum.IRA));
        formControls['disability'].setValue(this.isSetIncomeType(incomeTypes, incomeTypesEnum.Disability));
      });
  }

  private isSetIncomeType(
    setIncomeTypes: GetBorrowerResponse.IncomeTypesEnum[] = [],
    incomeTypeEnum: GetBorrowerResponse.IncomeTypesEnum): boolean {

    return (setIncomeTypes.indexOf(incomeTypeEnum) >= 0);
  }

  private getControls(controlNames?: string[]): { [key: string]: AbstractControl } {
    let controls = {};
    let formControls = this.getForm().controls;

    if (controlNames) {
      controlNames.forEach(name => controls[name] = formControls[name]);
    } else {
      controls = formControls;
    }

    return controls;
  }

  private resetValuesAndValidation(
    controls: { [key: string]: AbstractControl },
    resetValues: boolean = true): void {

    Object.keys(controls).forEach(x => {
      let c = controls[x];

      if (resetValues) {
        if (typeof c.value === 'boolean') {
          c.setValue(false);
        } else {
          c.setValue('');
        }
      }

      c.markAsPristine();
      c.markAsUntouched();
    });
  }

  resetEmploymentInfoValuesAndValidation(resetValues: boolean = true): void {
      this.resetValuesAndValidation(this.employmentInfoControls, resetValues);
  }

  resetIncomeSourceValuesAndValidation(resetValues: boolean = true): void {
    this.resetValuesAndValidation(this.incomeSourceControls, resetValues);
  }
}
